const { VEHICLE_REGISTRATION_METHOD } = require("../../constants");

/**
 * Canonicalises a vehicleRegistration value to its registration-type string.
 *
 * The canonical shape is `{ type: <string> }` on both the frontend and deal-api.
 * Legacy leads may still carry the bare string ("Citroen"/"Myself"), and a historic
 * unguarded wrap (`{ type: vehicleRegistration }`) could produce malformed shapes such
 * as { type: {} } or { type: { type: "Citroen" } } that silently disabled every
 * registration-type check (carte grise, etc.). This resolves all of those:
 *
 *   undefined / null                -> undefined   (absent: registration not selected)
 *   "Citroen"                       -> "Citroen"   (legacy bare string)
 *   { type: "Citroen" }             -> "Citroen"   (canonical)
 *   { method: "Myself" }            -> "Myself"
 *   { type: { type: "Citroen" } }   -> "Citroen"   (self-heals recoverable nesting)
 *   { type: {} } / {} / nonsense    -> undefined   (present but unresolvable)
 *
 * There is deliberately NO fallback method: neither Citroën nor "Myself" can be assumed
 * (either may be unavailable for the journey/postal code, and defaulting would wrongly
 * charge or mis-address a customer who never chose). An unresolvable value therefore
 * resolves to undefined and is blocked at the summary page / order-create endpoint via
 * getIsVehicleRegistrationValid, which forces the customer to start over.
 *
 * @param {string|object|undefined|null} vehicleRegistration
 * @returns {string|undefined} the canonical registration type string, or undefined
 */
const getVehicleRegistrationType = vehicleRegistration => {
  if (vehicleRegistration === undefined || vehicleRegistration === null) {
    return undefined;
  }

  if (typeof vehicleRegistration === "string") {
    return vehicleRegistration;
  }

  if (typeof vehicleRegistration === "object") {
    return getVehicleRegistrationType(
      vehicleRegistration.type ?? vehicleRegistration.method
    );
  }

  return undefined;
};

/**
 * Wraps a vehicleRegistration value into the canonical `{ type: <string> }` shape,
 * or undefined when no registration has been resolved. Never returns `{ type: undefined }`
 * (which would serialise to `{}` and re-introduce the corruption).
 *
 * @param {string|object|undefined|null} vehicleRegistration
 * @returns {{ type: string }|undefined}
 */
const normalizeVehicleRegistration = vehicleRegistration => {
  const type = getVehicleRegistrationType(vehicleRegistration);

  return type ? { type } : undefined;
};

/**
 * True only when the value resolves to one of the two known registration methods
 * (Citroën or Myself). Absent, corrupt, or unknown values are invalid. Used to block
 * order creation on nonsense registration and force the customer to start over.
 *
 * @param {string|object|undefined|null} vehicleRegistration
 * @returns {boolean}
 */
const getIsVehicleRegistrationValid = vehicleRegistration => {
  const type = getVehicleRegistrationType(vehicleRegistration);

  return (
    type === VEHICLE_REGISTRATION_METHOD.CITROEN ||
    type === VEHICLE_REGISTRATION_METHOD.MYSELF
  );
};

module.exports = {
  getVehicleRegistrationType,
  normalizeVehicleRegistration,
  getIsVehicleRegistrationValid,
};
